import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PencilIcon, UserPlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Field, Input } from '@headlessui/react';
import clsx from 'clsx';
import Table from '../shared/Table';
import ReusableButton from '../shared/ReusableButton';
import Pagination from '../shared/Pagination';
import useUsers from '../../hooks/useUsers';
import { useTranslation } from 'react-i18next';

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  languagePreference: string;
}

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const { users, totalCount, totalPages, loading, error } = useUsers({
    search: debouncedSearch,
    page: currentPage,
    limit: 20,
  });

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleEdit = async (id: number) => {
    navigate(`/edit-user/${id}`);
  };

  const key = (user: User) => user.id;
  const row = (user: User) => (
    <>
      {/* <td className="border px-2 py-2">{user.id}</td> */}
      <td className="border px-2 py-2 break-words">{user.name}</td>
      <td className="border px-2 py-2 break-all">{user.email}</td>
      <td className="border px-2 py-2 capitalize w-28">{user.role}</td>
      <td className="border px-2 py-2 w-24">
        <ReusableButton
          onClick={() => handleEdit(user.id)}
          icon={<PencilIcon className="size-5" aria-hidden="true" />}
          label={t('common.edit')}
          className="my-1"
        />
      </td>
    </>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('users.title')}</h1>
          <p className="text-slate-600 mt-1">{t('users.subtitle')}</p>
        </div>
        <button
          onClick={() => navigate('/add-user')}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl shadow-sm hover:shadow-md transition-all"
        >
          <UserPlusIcon className="size-5" />
          <span>{t('users.add')}</span>
        </button>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <Field>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder={t('users.search')}
              value={searchTerm}
              onChange={handleSearchChange}
              className={clsx(
                'block w-full rounded-lg border border-slate-300 bg-white py-3 pl-12 pr-4 text-slate-900',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                'placeholder:text-slate-400'
              )}
            />
          </div>
        </Field>
      </div>

      {/* Content */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="mt-2 text-slate-600">{t('users.loading')}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <>
          {users.length > 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <Table
                  headers={[t('users.name'), t('users.email'), t('users.role'), t('users.actions')]}
                  data={users}
                  renderRow={row}
                  keyExtractor={key}
                />
              </div>
              <div className="border-t border-slate-200 px-6 py-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  onPageChange={handlePageChange}
                  itemsPerPage={10}
                />
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
              <UserPlusIcon className="mx-auto h-12 w-12 text-slate-400" />
              <h3 className="mt-4 text-lg font-semibold text-slate-900">
                {searchTerm ? t('users.noResults') : t('users.noUsers')}
              </h3>
              {!searchTerm && (
                <p className="mt-2 text-slate-600">Get started by creating a new user</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Dashboard;
